import { useEffect, useState } from 'react';

/**
 * Convite para instalar o app.
 *
 * Dois caminhos, porque são dois sistemas com regras diferentes:
 *
 * - **Android/Chrome** dispara `beforeinstallprompt`. O evento precisa ser
 *   **guardado** (com `preventDefault`) e disparado depois, dentro de um
 *   gesto — se deixarmos o navegador mostrar o banner dele, perdemos o
 *   controle de quando e onde o convite aparece.
 * - **iOS/Safari** não tem API nenhuma: instalar é "Compartilhar → Adicionar
 *   à Tela de Início". Só resta explicar, e só para quem ainda não instalou.
 *
 * Em modo standalone nada disso aparece: já está instalado.
 */

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** Quem dispensou o convite não deve vê-lo de novo. */
const DISMISSED_KEY = 'bingo.instalar.dispensado';

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function remember(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    // Modo privado: o convite volta na próxima sessão. Tudo bem.
  }
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;

  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

/** iPhone/iPad no Safari — onde a instalação é manual. */
export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  // Chrome e Firefox no iOS também são WebKit, mas não instalam PWA; só o
  // Safari tem "Adicionar à Tela de Início".
  const safari = !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);

  return ios && safari;
}

export type InstallHint = 'none' | 'prompt' | 'ios';

export interface InstallState {
  hint: InstallHint;
  install: () => Promise<void>;
  dismiss: () => void;
}

export function useInstallPrompt(): InstallState {
  const [deferred, setDeferred] = useState<InstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => wasDismissed());
  const [standalone] = useState(() => isStandalone());

  useEffect(() => {
    if (standalone) return;

    const onPrompt = (event: Event) => {
      // Sem isto o Chrome mostra o banner dele, na hora que ele escolher.
      event.preventDefault();
      setDeferred(event as InstallPromptEvent);
    };

    const onInstalled = () => {
      setDeferred(null);
      remember();
      setDismissed(true);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [standalone]);

  const hint: InstallHint =
    standalone || dismissed ? 'none' : deferred ? 'prompt' : isIosSafari() ? 'ios' : 'none';

  return {
    hint,
    async install() {
      if (!deferred) return;

      await deferred.prompt();
      await deferred.userChoice;

      // O evento é de uso único: depois de disparado não serve mais.
      setDeferred(null);
      remember();
      setDismissed(true);
    },
    dismiss() {
      remember();
      setDismissed(true);
    },
  };
}
